import React from 'react';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../common/ui/Button';
import { Github, Google, Linkedin } from '../../common/icons';
import { captureCustomEvent } from '../../../utils/posthogUtils';
import { captureAppException } from '../../../utils/errorCaptureUtils';

interface OAuthButtonGroupProps {
  onLoadingChange: (isLoading: boolean) => void;
  onError: (error: string | null) => void;
  source?: string; // Optional source for tracking/redirect logic
  redirectUrlPath?: string; // e.g., '/register' or '/login' or specific callback path
  layoutStyle?: 'vertical' | 'horizontalIcons'; // New prop for layout style
}

export const OAuthButtonGroup: React.FC<OAuthButtonGroupProps> = ({
  onLoadingChange,
  onError,
  source = 'unknown',
  redirectUrlPath = '/', // Default redirect path if not specified
  layoutStyle = 'vertical' // Default to vertical layout
}) => {

  // Helper to construct the final redirect URL - REVISED
  const getRedirectURL = () => {
    let url = redirectUrlPath;
    // Check if redirectUrlPath is already a full URL
    if (!redirectUrlPath.startsWith('http')) {
      url = `${window.location.origin}${redirectUrlPath.startsWith('/') ? redirectUrlPath : '/' + redirectUrlPath}`;
    }
    
    // Append source as a query parameter if needed and if it's not already in a fully provided URL
    // This part might need refinement: if redirectUrlPath is a full URL, we assume source is handled by caller if necessary.
    if (source !== 'unknown' && !redirectUrlPath.startsWith('http')) {
      // Ensure we append correctly whether the base URL already has query params or not
      url += url.includes('?') ? '&' : '?';
      url += `source=${encodeURIComponent(source)}`;
    }
    return url;
  };

  const handleOAuthSignIn = async (provider: 'linkedin_oidc' | 'github' | 'google') => {
    onLoadingChange(true);
    onError(null);
    captureCustomEvent('oauth_button_clicked', { eventProperties: { provider, source } });

    try {
      const redirectTo = getRedirectURL();
      
      let options: any = { redirectTo };
      if (provider === 'linkedin_oidc') {
        options.queryParams = {
          access_type: 'offline',
          prompt: 'consent',
          scope: 'openid email profile',
        };
      } else if (provider === 'google') {
         options.queryParams = {
           scope: 'openid email profile',
           access_type: 'offline',
           prompt: 'consent',
         };
      }
      // Github doesn't need extra queryParams here

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options,
      });

      if (error) throw error;
      if (!data.url) throw new Error('No OAuth URL returned from Supabase');
      
      // Redirect the user to the provider's authentication page
      window.location.href = data.url;
      // Note: Loading state might not be set back to false here as the page redirects.
      // The parent component might need to handle loading state persistence if necessary.

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : `Failed to sign in with ${provider}`;
      captureAppException(err, {
        tags: {
          context: 'oauth_button_group',
          action: 'signInWithOAuth',
          provider: provider,
          source: source
        }
      });
      console.error(`OAuth Error (${provider}):`, errorMessage);
      onError(errorMessage);
      onLoadingChange(false); // Ensure loading is stopped on error
    }
  };

  if (layoutStyle === 'horizontalIcons') {
    return (
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <Button
          variant="outline"
          className="w-full sm:flex-1 sm:min-w-0 border-greyscale-500 text-greyscale-800 hover:bg-gray-50 py-2 px-3 flex items-center justify-center"
          onClick={() => handleOAuthSignIn('linkedin_oidc')}
        >
          <Linkedin className="w-7 h-7" />
          <span className="truncate ml-1.5">LinkedIn</span>
        </Button>
        <Button
          variant="outline"
          className="w-full sm:flex-1 sm:min-w-0 border-greyscale-500 text-greyscale-800 hover:bg-gray-50 py-2 px-3 flex items-center justify-center"
          onClick={() => handleOAuthSignIn('github')}
        >
          <Github className="w-7 h-7" />
          <span className="truncate ml-1.5">GitHub</span>
        </Button>
        <Button
          variant="outline"
          className="w-full sm:flex-1 sm:min-w-0 border-greyscale-500 text-greyscale-800 hover:bg-gray-50 py-2 px-3 flex items-center justify-center"
          onClick={() => handleOAuthSignIn('google')}
        >
          <Google className="w-7 h-7" />
          <span className="truncate ml-1.5">Google</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Button
        variant="outline"
        className="w-full border-greyscale-500 text-greyscale-800"
        onClick={() => handleOAuthSignIn('linkedin_oidc')}
      >
        <Linkedin className="mr-4" />
        Continue with LinkedIn
      </Button>

      <Button
        variant="outline"
        className="w-full border-greyscale-500 text-greyscale-800"
        onClick={() => handleOAuthSignIn('github')}
      >
        <Github className="mr-4" />
        Continue with GitHub
      </Button>

      <Button
        variant="outline"
        className="w-full border-greyscale-500 text-greyscale-800"
        onClick={() => handleOAuthSignIn('google')}
      >
        <Google className="mr-4" />
        Continue with Google
      </Button>
    </div>
  );
}; 